import { isTrackingSuppressed } from "./privacyConsent";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const PIXEL_ID = "986942605706922";
const AM_EMAIL_KEY = "ccp_am_email";
const EXTERNAL_ID_KEY = "ccp_external_id";

export function getPixelId(): string {
  return PIXEL_ID;
}

function generateUuid(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  // RFC4122-ish fallback
  const rnd = (n: number) => {
    const bytes = new Uint8Array(n);
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < n; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    return bytes;
  };
  const b = rnd(16);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b).map((x) => x.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
}

export function getExternalId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    let id = window.localStorage.getItem(EXTERNAL_ID_KEY);
    if (!id) {
      id = generateUuid();
      window.localStorage.setItem(EXTERNAL_ID_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

function normalizeField(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "");
}

let lastInitPayload: string | undefined;

function initPixelOnce(payload: Record<string, string>) {
  const key = JSON.stringify(payload);
  if (key === lastInitPayload) return;
  lastInitPayload = key;
  try {
    window.fbq!("init", PIXEL_ID, payload);
  } catch {
    // silently fail
  }
}

export function ensurePixelIdentity() {
  if (typeof window === "undefined") return;
  if (isTrackingSuppressed()) return;
  if (typeof window.fbq !== "function") return;
  const external_id = getExternalId();
  if (!external_id) return;
  initPixelOnce({ external_id });
}


export function trackEvent(event: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  if (isTrackingSuppressed()) return;
  if (typeof window.fbq !== "function") return;
  try {
    if (params) {
      window.fbq("track", event, params);
    } else {
      window.fbq("track", event);
    }
  } catch {
    // silently fail if pixel is blocked or unavailable
  }
}

export function trackCustomEvent(event: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  if (isTrackingSuppressed()) return;
  if (typeof window.fbq !== "function") return;
  try {
    if (params) {
      window.fbq("trackCustom", event, params);
    } else {
      window.fbq("trackCustom", event);
    }
  } catch {
    // silently fail
  }
}

export function trackPageView() {
  trackEvent("PageView");
}

export interface AdvancedMatchingData {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  st?: string;
  zp?: string;
  country?: string;
}

export function setAdvancedMatching(data: AdvancedMatchingData) {
  if (typeof window === "undefined") return;
  if (isTrackingSuppressed()) return;
  if (typeof window.fbq !== "function") return;
  try {
    const payload: Record<string, string> = {};

    if (data.em) payload.em = data.em.trim().toLowerCase();
    if (data.ph) {
      const digits = data.ph.replace(/\D/g, "");
      if (digits) payload.ph = digits;
    }
    if (data.fn) payload.fn = normalizeField(data.fn);
    if (data.ln) payload.ln = normalizeField(data.ln);
    if (data.ct) payload.ct = normalizeField(data.ct);
    if (data.st) payload.st = normalizeField(data.st);
    if (data.zp) payload.zp = normalizeField(data.zp);
    if (data.country) payload.country = normalizeField(data.country);

    const external_id = getExternalId();
    if (external_id) payload.external_id = external_id;

    initPixelOnce(payload);

    if (payload.em) {
      try {
        sessionStorage.setItem(AM_EMAIL_KEY, payload.em);
      } catch {
        // ignore storage errors
      }
    }
  } catch {
    // silently fail
  }
}
